/**
 * Storage Utilities
 *
 * Wrapper around localStorage with typed keys and error handling.
 * Auth tokens are stored in multiple places for maximum compatibility:
 * 
 * 1. localStorage - fast sync access
 * 2. IndexedDB - reliable async persistence (survives Safari eviction)
 * 
 * PWA Authentication:
 * Users authenticate directly in PWA (no token transfer from Safari).
 * Tokens are obfuscated with device fingerprint for defense-in-depth.
 */

import { z } from "zod";
import { storageLog } from "./logger";

const PREFIX = "one-church:";

// =============================================================================
// TOKEN OBFUSCATION (defense-in-depth against simple XSS exfiltration)
// =============================================================================

/**
 * Token obfuscation using Web Crypto API.
 * 
 * IMPORTANT: This is NOT encryption for security at rest - the key is derived
 * from a fixed value and is accessible in JS. However, it provides:
 * 
 * 1. Defense-in-depth: Tokens are not in plaintext, preventing simple
 *    `localStorage.getItem()` exfiltration via XSS
 * 2. Opaque storage: Tokens look like random data, not JWTs
 * 3. Integrity: Any tampering will cause decryption to fail
 * 
 * For Telegram Mini Apps and PWAs where httpOnly cookies aren't feasible,
 * this provides meaningful protection while maintaining UX.
 */

const OBFUSCATION_SALT = "one-church-token-v1";
const OBFUSCATION_ITERATIONS = 100000;

let cachedKey: CryptoKey | null = null;

// =============================================================================
// IN-MEMORY TOKEN CACHE
// =============================================================================

/**
 * In-memory cache for plaintext tokens.
 * 
 * This solves the sync/async mismatch:
 * - saveTokens() is async (uses crypto for obfuscation)
 * - getStoredTokens() needs to be sync (for use in fetchExchange)
 * 
 * The cache is populated when:
 * 1. saveTokens() is called (new login)
 * 2. getStoredTokensAsync() successfully deobfuscates tokens
 * 
 * This is NOT a security weakness - tokens are already in memory
 * during the session. The cache just provides sync access.
 */
interface TokenCache {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

let tokenCache: TokenCache | null = null;

/**
 * Set in-memory token cache (called by saveTokens and getStoredTokensAsync).
 */
export function setTokenCache(tokens: TokenCache | null): void {
  tokenCache = tokens;
  storageLog.debug("Token cache updated:", tokens ? "set" : "cleared");
}

/**
 * Get tokens from in-memory cache.
 */
export function getTokenCache(): TokenCache | null {
  return tokenCache;
}

/**
 * Derive a key for token obfuscation.
 * Uses device fingerprint + fixed salt for consistent key across sessions.
 */
async function getObfuscationKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;
  
  // Device fingerprint - not for security, just for key derivation consistency
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
  ].join("|");
  
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(fingerprint + OBFUSCATION_SALT),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  
  cachedKey = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: encoder.encode(OBFUSCATION_SALT),
      iterations: OBFUSCATION_ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
  
  return cachedKey;
}

/**
 * Obfuscate a token value for storage.
 * Returns base64-encoded encrypted data.
 */
async function obfuscateToken(token: string): Promise<string> {
  try {
    const key = await getObfuscationKey();
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      encoder.encode(token)
    );
    
    // Combine IV + encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    
    // Base64 encode for storage
    return btoa(String.fromCharCode(...combined));
  } catch (err) {
    storageLog.warn("Token obfuscation failed, storing plaintext:", err);
    // Fallback to plaintext if crypto fails (old browsers)
    return token;
  }
}

/**
 * Deobfuscate a token value from storage.
 * Returns null if decryption fails (tampered data).
 */
async function deobfuscateToken(encoded: string): Promise<string | null> {
  try {
    // Check if it's a JWT (plaintext fallback or legacy)
    if (encoded.startsWith("eyJ")) {
      return encoded;
    }
    
    const key = await getObfuscationKey();
    const combined = Uint8Array.from(atob(encoded), c => c.charCodeAt(0));
    
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      encrypted
    );
    
    return new TextDecoder().decode(decrypted);
  } catch (err) {
    storageLog.warn("Token deobfuscation failed:", err);
    // If decryption fails, the data was tampered with or is legacy plaintext
    // Try to return as-is if it looks like a JWT
    if (encoded.startsWith("eyJ")) {
      return encoded;
    }
    return null;
  }
}

// =============================================================================
// INDEXEDDB (async, for tokens - survives Safari storage eviction)
// =============================================================================

const IDB_NAME = "one-church-auth";
const IDB_STORE = "tokens";
const IDB_VERSION = 1;

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(IDB_NAME, IDB_VERSION);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(IDB_STORE, { keyPath: "key" });
      };
    } catch (e) {
      reject(e);
    }
  });
}

function idbGet(key: string): Promise<string | null> {
  return openIDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readonly");
        const store = tx.objectStore(IDB_STORE);
        const req = store.get(key);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => {
          db.close();
          resolve(req.result?.value ?? null);
        };
      })
  );
}

function idbSet(key: string, value: string): Promise<void> {
  return openIDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readwrite");
        const store = tx.objectStore(IDB_STORE);
        store.put({ key, value });
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => reject(tx.error);
      })
  );
}

function idbRemove(key: string): Promise<void> {
  return openIDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readwrite");
        const store = tx.objectStore(IDB_STORE);
        store.delete(key);
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => reject(tx.error);
      })
  );
}

// =============================================================================
// STORAGE KEYS & CONSTANTS
// =============================================================================

export const STORAGE_KEYS = {
  ACCESS_TOKEN: "access_token",
  REFRESH_TOKEN: "refresh_token",
  TOKEN_EXPIRES_AT: "token_expires_at",
  REMEMBERED_EMAIL: "remembered_email",
  LAST_EMAILS: "last_emails",
} as const;

const LAST_EMAILS_MAX = 5;

// Token refresh buffer (5 minutes before expiry)
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000;

// =============================================================================
// GENERIC STORAGE
// =============================================================================

export const storage = {
  /**
   * Get a value from storage.
   * Use getWithSchema for type-safe validation.
   */
  get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(PREFIX + key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  },

  /**
   * Get a value from storage with Zod schema validation.
   * Returns null if validation fails.
   */
  getWithSchema<T>(key: string, schema: z.ZodSchema<T>): T | null {
    try {
      const item = localStorage.getItem(PREFIX + key);
      if (!item) return null;
      
      const parsed = JSON.parse(item);
      const result = schema.safeParse(parsed);
      return result.success ? result.data : null;
    } catch {
      return null;
    }
  },

  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(PREFIX + key, JSON.stringify(value));
    } catch {
      // Storage might be full or unavailable
    }
  },

  remove(key: string): void {
    try {
      localStorage.removeItem(PREFIX + key);
    } catch {
      // Ignore errors
    }
  },

  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch {
      // Ignore errors
    }
  },
};

// =============================================================================
// REMEMBERED EMAIL (PWA + browser, only when user opts in)
// =============================================================================

/**
 * Get the last remembered email (user opted in via "Запомнить email").
 */
export function getRememberedEmail(): string | null {
  const value = storage.get<string>(STORAGE_KEYS.REMEMBERED_EMAIL);
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Save email as remembered (only call when user has opted in).
 * Also updates the list of last emails for autocomplete.
 */
export function setRememberedEmail(email: string): void {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed || !trimmed.includes("@")) return;
  storage.set(STORAGE_KEYS.REMEMBERED_EMAIL, trimmed);
  const list = getLastEmails();
  const without = list.filter((e) => e !== trimmed);
  const updated = [trimmed, ...without].slice(0, LAST_EMAILS_MAX);
  storage.set(STORAGE_KEYS.LAST_EMAILS, updated);
}

/**
 * Get last N emails for autocomplete (datalist).
 */
export function getLastEmails(): string[] {
  const value = storage.get<string[]>(STORAGE_KEYS.LAST_EMAILS);
  return Array.isArray(value) ? value.slice(0, LAST_EMAILS_MAX) : [];
}

// =============================================================================
// TOKEN STORAGE
// =============================================================================

/**
 * Schema for stored tokens with runtime validation.
 * Ensures data integrity when loading from storage.
 */
export const StoredTokensSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  expiresAt: z.union([z.string().min(1), z.number()]).transform((val) => 
    typeof val === "number" ? new Date(val).toISOString() : val
  ),
});

export type StoredTokens = z.infer<typeof StoredTokensSchema>;

/**
 * Save auth tokens to storage (localStorage + IndexedDB + in-memory cache).
 * 
 * SECURITY: Tokens are obfuscated before storage to prevent simple XSS exfiltration.
 * In-memory cache keeps plaintext for sync access during the session.
 */
export async function saveTokens(tokens: {
  accessToken: string;
  refreshToken: string;
  expiresAt: Date | string;
}): Promise<{ localStorage: boolean; indexedDB: boolean }> {
  storageLog.debug("saveTokens called");
  
  const result = { localStorage: false, indexedDB: false };
  
  const expiresAtStr =
    typeof tokens.expiresAt === "string"
      ? tokens.expiresAt
      : tokens.expiresAt.toISOString();
  
  // Update in-memory cache immediately (for sync access)
  setTokenCache({
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: expiresAtStr,
  });
  
  // Obfuscate tokens before storage (defense-in-depth against XSS)
  const [obfuscatedAccess, obfuscatedRefresh] = await Promise.all([
    obfuscateToken(tokens.accessToken),
    obfuscateToken(tokens.refreshToken),
  ]);
  
  // 1. localStorage (sync, fast) - store obfuscated tokens
  storageLog.debug("Saving to localStorage (obfuscated)...");
  try {
    storage.set(STORAGE_KEYS.ACCESS_TOKEN, obfuscatedAccess);
    storage.set(STORAGE_KEYS.REFRESH_TOKEN, obfuscatedRefresh);
    storage.set(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAtStr);
    
    // Verify localStorage
    const lsVerify = localStorage.getItem(PREFIX + STORAGE_KEYS.ACCESS_TOKEN);
    result.localStorage = !!lsVerify;
    storageLog.debug("localStorage verification:", result.localStorage ? "OK" : "FAILED");
  } catch (err) {
    storageLog.warn("localStorage error:", err);
  }
  
  // 2. IndexedDB (async, reliable) - store obfuscated tokens
  storageLog.debug("Saving to IndexedDB (obfuscated)...");
  const idbKey = (k: string) => PREFIX + k;
  try {
    await Promise.all([
      idbSet(idbKey(STORAGE_KEYS.ACCESS_TOKEN), obfuscatedAccess),
      idbSet(idbKey(STORAGE_KEYS.REFRESH_TOKEN), obfuscatedRefresh),
      idbSet(idbKey(STORAGE_KEYS.TOKEN_EXPIRES_AT), expiresAtStr),
    ]);
    result.indexedDB = true;
    storageLog.debug("IndexedDB save complete");
  } catch (err) {
    storageLog.warn("IndexedDB save error:", err);
  }
  
  storageLog.debug("All storage saves complete:", result);
  return result;
}

/**
 * Get stored tokens (sync).
 * 
 * Checks sources in priority order:
 * 1. In-memory cache (populated by saveTokens or getStoredTokensAsync)
 * 2. localStorage (for legacy plaintext tokens only)
 * 
 * SECURITY: Uses Zod schema validation to ensure data integrity.
 * Invalid or tampered data is rejected, preventing:
 * - Invalid expiresAt causing isTokenExpired() to malfunction (NaN → never expired)
 * - Corrupted tokens being sent to API
 */
export function getStoredTokens(): StoredTokens | null {
  // 1. Check in-memory cache first (populated by saveTokens or getStoredTokensAsync)
  const cached = getTokenCache();
  if (cached) {
    // Validate cached tokens
    const result = StoredTokensSchema.safeParse(cached);
    if (result.success) {
      return result.data;
    }
    // Invalid cache, clear it
    setTokenCache(null);
  }

  // 2. Fallback to localStorage (for legacy plaintext tokens)
  const accessToken = storage.get<string>(STORAGE_KEYS.ACCESS_TOKEN);
  const refreshToken = storage.get<string>(STORAGE_KEYS.REFRESH_TOKEN);
  const expiresAt = storage.get<string>(STORAGE_KEYS.TOKEN_EXPIRES_AT);

  if (!accessToken || !refreshToken || !expiresAt) {
    return null;
  }

  // Check if tokens look like JWTs (plaintext/legacy)
  // Obfuscated tokens start with base64 data, not "eyJ"
  if (!accessToken.startsWith("eyJ")) {
    // Tokens are obfuscated - sync function cannot deobfuscate
      // Return null - caller should use getStoredTokensAsync() or wait for cache
    storageLog.debug("Tokens are obfuscated, waiting for async deobfuscation");
    return null;
  }

  // Validate using Zod schema for runtime type safety
  const result = StoredTokensSchema.safeParse({
    accessToken,
    refreshToken,
    expiresAt,
  });

  if (!result.success) {
    storageLog.warn("Invalid tokens in localStorage:", result.error.message);
    return null;
  }

  return result.data;
}

/**
 * Get stored tokens (async). Checks multiple sources in priority order:
 * 1. In-memory cache (fastest)
 * 2. localStorage (with deobfuscation)
 * 3. IndexedDB (reliable, with deobfuscation)
 * 
 * SECURITY: Handles both obfuscated and legacy plaintext tokens.
 * Deobfuscation is required for tokens stored after the security update.
 * Successfully deobfuscated tokens are cached in memory for sync access.
 */
export async function getStoredTokensAsync(): Promise<StoredTokens | null> {
  storageLog.debug("getStoredTokensAsync called");
  
  // 0. Check in-memory cache first (fastest)
  const cached = getTokenCache();
  if (cached) {
    const cacheResult = StoredTokensSchema.safeParse(cached);
    if (cacheResult.success) {
      storageLog.debug("Tokens retrieved from in-memory cache");
      return cacheResult.data;
    }
    // Invalid cache, clear it
    setTokenCache(null);
  }
  
  // 1. Try localStorage (with deobfuscation)
  const idbKey = (k: string) => PREFIX + k;
  
  const lsAccessToken = storage.get<string>(STORAGE_KEYS.ACCESS_TOKEN);
  const lsRefreshToken = storage.get<string>(STORAGE_KEYS.REFRESH_TOKEN);
  const lsExpiresAt = storage.get<string>(STORAGE_KEYS.TOKEN_EXPIRES_AT);
  
  if (lsAccessToken && lsRefreshToken && lsExpiresAt) {
    storageLog.debug("Found tokens in localStorage, deobfuscating...");
    
    const [accessToken, refreshToken] = await Promise.all([
      deobfuscateToken(lsAccessToken),
      deobfuscateToken(lsRefreshToken),
    ]);
    
    if (accessToken && refreshToken) {
      const result = StoredTokensSchema.safeParse({
        accessToken,
        refreshToken,
        expiresAt: lsExpiresAt,
      });
      
      if (result.success) {
        storageLog.debug("Tokens deobfuscated from localStorage");
        // Update in-memory cache for sync access
        setTokenCache(result.data);
        return result.data;
      }
      storageLog.warn("Invalid tokens after deobfuscation:", result.error.message);
    } else {
      storageLog.warn("Token deobfuscation failed, tokens may be tampered");
      // Clear potentially tampered tokens
      clearTokens();
    }
  }
  storageLog.debug("No valid tokens in localStorage");
  
  // 2. Try IndexedDB - with deobfuscation
  try {
    const [idbAccessToken, idbRefreshToken, idbExpiresAt] = await Promise.all([
      idbGet(idbKey(STORAGE_KEYS.ACCESS_TOKEN)),
      idbGet(idbKey(STORAGE_KEYS.REFRESH_TOKEN)),
      idbGet(idbKey(STORAGE_KEYS.TOKEN_EXPIRES_AT)),
    ]);
    
    if (idbAccessToken && idbRefreshToken && idbExpiresAt) {
      storageLog.debug("Found tokens in IndexedDB, deobfuscating...");
      
      const [accessToken, refreshToken] = await Promise.all([
        deobfuscateToken(idbAccessToken),
        deobfuscateToken(idbRefreshToken),
      ]);
      
      if (accessToken && refreshToken) {
        const result = StoredTokensSchema.safeParse({
          accessToken,
          refreshToken,
          expiresAt: idbExpiresAt,
        });
        
        if (result.success) {
          storageLog.debug("Tokens deobfuscated from IndexedDB");
          // Update in-memory cache for sync access
          setTokenCache(result.data);
          // Sync to localStorage for faster access next time
          try {
            storage.set(STORAGE_KEYS.ACCESS_TOKEN, idbAccessToken);
            storage.set(STORAGE_KEYS.REFRESH_TOKEN, idbRefreshToken);
            storage.set(STORAGE_KEYS.TOKEN_EXPIRES_AT, idbExpiresAt);
          } catch {
            // Non-fatal: localStorage sync failed
          }
          return result.data;
        }
        storageLog.warn("Invalid tokens in IndexedDB:", result.error.message);
      }
    }
    storageLog.debug("No valid tokens in IndexedDB");
  } catch (err) {
    storageLog.debug("IndexedDB error:", err);
  }
  
  return null;
}

/**
 * Clear stored tokens (in-memory cache + localStorage + IndexedDB).
 */
export function clearTokens(): void {
  // Clear in-memory cache first
  setTokenCache(null);
  
  // Clear localStorage
  storage.remove(STORAGE_KEYS.ACCESS_TOKEN);
  storage.remove(STORAGE_KEYS.REFRESH_TOKEN);
  storage.remove(STORAGE_KEYS.TOKEN_EXPIRES_AT);
  
  // Clear IndexedDB (async, fire-and-forget)
  const idbKey = (k: string) => PREFIX + k;
  idbRemove(idbKey(STORAGE_KEYS.ACCESS_TOKEN)).catch((err) => {
    storageLog.debug("Non-fatal: IDB access token removal failed", err);
  });
  idbRemove(idbKey(STORAGE_KEYS.REFRESH_TOKEN)).catch((err) => {
    storageLog.debug("Non-fatal: IDB refresh token removal failed", err);
  });
  idbRemove(idbKey(STORAGE_KEYS.TOKEN_EXPIRES_AT)).catch((err) => {
    storageLog.debug("Non-fatal: IDB token expiry removal failed", err);
  });
}

/**
 * Check if access token is expired (with buffer).
 */
export function isTokenExpired(expiresAt: string): boolean {
  const expiry = new Date(expiresAt).getTime();
  return Date.now() > expiry - TOKEN_REFRESH_BUFFER;
}
